import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import sbpService from '../../service/sbp.service';
import backendApiService from '../../service/backend-api.service';
import PaymentRetryButton from '../../components/common/PaymentRetryButton';

// Параметры из адресной строки может подставить кто угодно — показываем только известные коды.
const ERROR_TEXTS = {
  cancelled: 'платёж отменён',
  canceled: 'платёж отменён',
  expired: 'время на оплату истекло',
  timeout: 'время на оплату истекло',
  declined: 'банк отклонил платёж',
  rejected: 'банк отклонил платёж',
  insufficient_funds: 'недостаточно средств',
};

const PaymentFail = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const rawOrderId = searchParams.get('order_id');
  const orderId = /^[\w-]{1,80}$/.test(rawOrderId || '') ? rawOrderId : null;
  const errorKey = String(searchParams.get('error') || '').toLowerCase();
  const error = Object.prototype.hasOwnProperty.call(ERROR_TEXTS, errorKey)
    ? ERROR_TEXTS[errorKey]
    : null;
  const [paymentRetry, setPaymentRetry] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [qrCode, setQrCode] = useState(null);

  const loadPaymentRetry = useCallback(async () => {
    if (!orderId) {
      return;
    }
    const result = await sbpService.checkPaymentStatus(orderId);
    if (result?.success) {
      setPaymentRetry(result.paymentRetry || null);
      setOrderStatus(result.status || null);
    }
  }, [orderId]);

  useEffect(() => {
    loadPaymentRetry();
  }, [loadPaymentRetry]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {/* Иконка ошибки */}
        <div className="mx-auto w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
          <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>

        {/* Заголовок */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Платеж не прошел
        </h1>

        {/* Описание */}
        <p className="text-gray-600 mb-6">
          К сожалению, произошла ошибка при обработке платежа. Попробуйте еще раз или обратитесь в поддержку.
        </p>

        {/* Детали ошибки */}
        {orderId && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="text-sm text-gray-500 mb-2">Детали заказа:</div>
            <div className="text-lg font-semibold text-gray-900">
              Заказ №{orderId}
            </div>
            {error && (
              <div className="text-sm text-red-600 mt-2">
                Ошибка: {error}
              </div>
            )}
          </div>
        )}

        {qrCode ? (
          <div className="mb-6">
            <p className="text-gray-700 mb-3">
              Новый QR-код для оплаты: отсканируйте его в приложении банка.
            </p>
            <img
              src={`data:image/png;base64,${qrCode}`}
              alt="QR-код для оплаты"
              className="w-48 h-48 mx-auto"
            />
            <p className="text-sm text-gray-500 mt-3">
              После оплаты Steam пополнится автоматически.
            </p>
          </div>
        ) : orderStatus !== 'SUCCESS' ? (
          <PaymentRetryButton
            paymentRetry={paymentRetry}
            failed={orderStatus === 'FAILED'}
            onCheck={() => backendApiService.checkRetrySteamPayment(orderId)}
            onCreate={() => backendApiService.retrySteamPayment(orderId)}
            onQrCode={setQrCode}
            onChanged={loadPaymentRetry}
            support={
              <a
                href={process.env.REACT_APP_SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800"
              >
                Написать в техподдержку
              </a>
            }
            className="mb-6 flex flex-col items-center gap-2"
          />
        ) : null}

        {/* Информационное сообщение */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="text-left">
              <p className="text-red-800 text-sm font-medium mb-1">
                Ошибка при обработке платежа
              </p>
              <p className="text-red-700 text-xs">
                Если проблема повторяется, сообщите номер заказа <span className="font-mono font-bold">{orderId || 'XXXXX'}</span> в техподдержку: <a href={process.env.REACT_APP_SUPPORT_URL} className="underline hover:no-underline">@tp_trendy_eng_shop</a>
              </p>
            </div>
          </div>
        </div>

        {/* Кнопки действий */}
        <div className="space-y-3">
          <button
            onClick={() => window.location.href = '/steam'}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Попробовать снова
          </button>
          
          <button
            onClick={() => window.location.href = '/'}
            className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
          >
            На главную
          </button>
        </div>

        {/* Дополнительная информация */}
        <div className="mt-8 text-sm text-gray-500">
          <p>Нужна помощь? Обратитесь в поддержку:</p>
          <a 
            href={process.env.REACT_APP_SUPPORT_URL} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 font-semibold"
          >
            @tp_trendy_eng_shop
          </a>
        </div>
      </div>
    </div>
  );
};

export default PaymentFail;
